/**
 * Sync activeTab <-> URL pathname.
 * Provides a wrapped setActiveTab that also navigates.
 * Reads URL on mount/change and updates state accordingly, enabling deep-links.
 */

import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export const TAB_TO_PATH = {
  // Home = strona powitalna (default)
  home: '/',
  add: '/napiwki',
  communicator: '/komunikator',
  summary: '/podsumowanie',
  reports: '/raporty',
  waiters: '/pracownicy',
  billard: '/billard',
  glass: '/szkla',
  kitchen_losses: '/straty',
  kitchen_losses_add: '/straty/dodaj',
  kitchen_losses_reports: '/straty/raporty',
  kitchen_losses_products: '/straty/produkty',
  warehouse_protocols: '/straty/protokoly',
  beer_cleaning: '/czyszczenie-piwa',
  training: '/szkolenia',
  materials: '/materialy',
  briefing: '/informacje-dnia',
  sales: '/sprzedaz',
  promotions: '/promocje',
  marketing: '/marketing',
  deliveries: '/dostawy',
  clothing: '/odziez',
  graphic_projects: '/grafika',
  export: '/eksport',
  changelog: '/nowosci',
  tutorial: '/samouczek',
  manager: '/manager',
  recruitment: '/rekrutacja-dni-probne',
  recruitment_apps: '/rekrutacja-internetowa',
  reminders: '/przypomnienia',
  manager_analytics: '/analiza-managerska',
  manager_performance: '/manager-performance',
  team_engagement: '/zaangazowanie-zespolu',
  cash: '/kasa',
  inventory: '/inwentaryzacja',
  monthly_closing: '/zamkniecie-miesiaca',
  reviews: '/opinie',
  delikatesy: '/delikatesy',
  forms: '/formularze',
  development: '/zespol-rozwoju',
  jerry_drive: '/jerry-drive',
  warehouse: '/magazyn',
  restaurant_matters: '/sprawy-restauracji',
  invoices: '/faktury',
  events_catering: '/konfigurator-przyjec',
  club_admin: '/klub-admin',
  delivery_survey: '/dostawa-ankieta',
  daily_settlement: '/rozliczenia-dzienne',
  files: '/pliki',
  settings: '/ustawienia',
};

const PATH_TO_TAB = Object.fromEntries(
  Object.entries(TAB_TO_PATH).map(([tab, path]) => [path, tab]),
);

// Legacy path aliases — redirect old URLs after routing change
const LEGACY_PATH_ALIASES = {
  '/dodaj-napiwek': 'add',
};

export const useTabRouting = (activeTab, setActiveTabInternal) => {
  const navigate = useNavigate();
  const location = useLocation();

  // URL -> state (handles deep-links, back/forward buttons)
  useEffect(() => {
    const tab = PATH_TO_TAB[location.pathname] || LEGACY_PATH_ALIASES[location.pathname];
    if (tab && tab !== activeTab) {
      setActiveTabInternal(tab);
    }
    // If landed on legacy /komunikator — normalise to '/'
    if (LEGACY_PATH_ALIASES[location.pathname]) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // state -> URL (wrapped setter)
  const setActiveTab = useCallback(
    (tab) => {
      setActiveTabInternal(tab);
      const path = TAB_TO_PATH[tab] || '/';
      if (path !== location.pathname) {
        navigate(path);
      }
    },
    [navigate, location.pathname, setActiveTabInternal],
  );

  return setActiveTab;
};
